import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, Inject, NgZone, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { TranslocoCurrencyPipe, TranslocoDatePipe, TranslocoDecimalPipe, TranslocoPercentPipe } from '@jsverse/transloco-locale';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { MultiSelectModule } from 'primeng/multiselect';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { BehaviorSubject, debounceTime, Subject, takeUntil } from 'rxjs';
import { StationBalancing, StationBalancingDetails } from '../../interfaces/balancing';
import { Column } from '../../interfaces/table';
import { DurationPipe } from '../../pipes/duration.pipe';
import { ConfirmDialogsService } from '../../services/confirm-dialogs.service';
import { NotificationsService } from '../../services/notifications.service';
import { getPathKey } from '../../utilities/util';
import { DeltaDisplayComponent } from '../delta-display-component/delta-display-component';
import { ActivatedRoute, Router } from '@angular/router';
import { WebsocketService } from '../../services/websocket.service';
import { Station } from '../../interfaces/station';
import { Tag } from 'primeng/tag';
import { LongPressDirective } from '../../directives/long-press.directive';


type MAIN_I = StationBalancing
type MAIN_I_2 = StationBalancingDetails

@Component({
  selector: 'app-admin-balancing-view',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    RippleModule,
    InputTextModule,
    TranslocoDirective,
    DialogModule,
    FloatLabelModule,
    InputNumberModule,
    MenuModule,
    MultiSelectModule,
    SelectModule,
    TextareaModule,
    SkeletonModule,
    LongPressDirective
  ],
  providers: [TranslocoDatePipe, TranslocoCurrencyPipe, TranslocoDecimalPipe, TranslocoPercentPipe, DurationPipe],
  templateUrl: './admin-balancing-view.component.html',
  styleUrl: './admin-balancing-view.component.scss'
})
export class AdminBalancingViewComponent implements OnDestroy, AfterViewInit {
  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- CONFIGURAZIONE BASE -------------------------------------------
  //^ --------------------------------------------------------------------------------------

  //? TRADUZIONI
  TITLE = "balancing"
  SEARCH_PLACEHOLDER = "searchStations"
  SEARCH_PLACEHOLDER_2 = "searchRecords"

  //? DATI
  KEY_NAME = 'stationId'
  GET_DATA_ENDPOINT = "getStationsBalancing"
  KEY_NAME_2 = 'historyId'
  GET_DATA_ENDPOINT_2 = "getRebalancingHistoryList"
  GET_RECORD_ENDPOINT_2 = "getRebalancingHistory"


  //! configurare columns, dataFilters e _batchMenuStructure 

  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- INIZIALIZZAZIONI ----------------------------------------------
  //^ --------------------------------------------------------------------------------------

  //? DATI E TABELLE
  columns: Column[] = [
    { field: 'id', header: 'id', visible: false },
    { field: 'code', header: 'stationCode', visible: true },
    { field: 'description', header: 'stationDescription', visible: true },
    { field: 'eodAmount', header: 'actualBikesAmount', visible: true },
    // { field: 'predictedAmount', header: 'predictedAmountToday', visible: true, },
    { field: 'predictedAmountTomorrow', header: 'predictedAmountTomorrow', visible: true, },
    { field: 'delta', header: 'delta', visible: true, wrapper: DeltaDisplayComponent, wrapperConfig: { value: "{{value}}" } },

  ]

  columns2: Column[] = [
    { field: 'historyId', header: 'id', visible: false },
    { field: 'creationTimestamp', header: 'timestamp', visible: true, pipe: ['date', { dateStyle: "short", timeStyle: "short" }] },
    { field: 'dayBase', header: 'dayBase', visible: true, pipe: ['date', { dateStyle: "full" }] },
    { field: 'dayTarget', header: 'dayTarget', visible: true, pipe: ['date', { dateStyle: "full" }] },
    { field: 'vehicleCapacity', header: 'vehicleCapacity', visible: true },
    {
      field: 'status', header: 'status', visible: true, nestedField: 'statusDetails.description', pipe: "translate", wrapper: Tag, wrapperConfig: {
        rounded: true,
        value: "{{value}}",
        icon: "{{statusDetails.icon}}",
        styleClass: "table-tag opacity",
        style: { "--bg-color": "{{statusDetails.color}}" },
      },
    },
  ]

  data: MAIN_I[] = []
  data2: MAIN_I_2[] = []

  get dataFilterFields() {
    return this.columns.filter(column => column.visible).map(column => column.field);
  }

  get dataFilterFields2() {
    return this.columns2.filter(column => column.visible).map(column => column.field);
  }

  selectedRows: any[] = []
  selectedRows2: any[] = []

  dataLoading$ = new BehaviorSubject<boolean>(false)
  dataLoadingUI$ = this.dataLoading$.pipe(
    debounceTime(200)
  );

  dataLoading2$ = new BehaviorSubject<boolean>(false)
  dataLoadingUI2$ = this.dataLoading2$.pipe(
    debounceTime(200)
  );

  rebalancingLoading$ = new BehaviorSubject<boolean>(false)
  rebalancingLoadingUI$ = this.rebalancingLoading$.pipe(
    debounceTime(200)
  );

  predictionsLoading$ = new BehaviorSubject<boolean>(false)
  predictionsLoadingUI$ = this.predictionsLoading$.pipe(
    debounceTime(200)
  );

  _dataTrackBy: (index: number, item: any) => any = (index: number, item: any) => {
    return item.KEY_NAME
  };

  _dataTrackBy2: (index: number, item: any) => any = (index: number, item: any) => {
    return item.KEY_NAME_2
  };

  //? VIEW
  formDialogVisible2: boolean = false;
  filtersDialogVisible: boolean = false;

  formDialogData2: MAIN_I_2 | undefined = undefined;

  //? TABS
  activeTab: string = '';

  //? ALTRO
  destroy$ = new Subject<void>();

  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- LIFE CYCLE - EVENTI -------------------------------------------
  //^ --------------------------------------------------------------------------------------

  constructor(
    private zone: NgZone,
    private http: HttpClient,
    private confirmDialogs: ConfirmDialogsService,
    private notifications: NotificationsService,
    private transloco: TranslocoService,
    private translocoDate: TranslocoDatePipe,
    private translocoCurrency: TranslocoCurrencyPipe,
    private translocoDecimal: TranslocoDecimalPipe,
    private translocoPercent: TranslocoPercentPipe,
    private durationPipe: DurationPipe,
    private router: Router,
    private route: ActivatedRoute,
    private wss: WebsocketService,
    @Inject('ENVIRONMENT') private ENV: any
  ) {
    this.refreshData()
    this.route.params.subscribe(params => {
      const tab = params['tab'];
      this.setActiveTab(tab, false);
      if (tab == "manage") {
        this.refreshData()
      } else {
        this.refreshData2()
      }
    });

    wss.eventsSubject$.pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (e) => {
        if (e.type == "REBALANCING_CALCULATED") {
          this.refreshData2()
        }
      }
    })
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
  }

  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- ALTRO ---------------------------------------------------------
  //^ --------------------------------------------------------------------------------------

  //? DATI E TABELLE
  refreshData() {
    this.dataLoading$.next(true)
    this.http.post(this.ENV.apiUrl + this.GET_DATA_ENDPOINT, {}).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resp: any) => {
        this.data = resp.data;
        this.selectedRows = []
        this.dataLoading$.next(false)
      },
      error: () => {
        this.dataLoading$.next(false)
      }
    })
  }

  refreshData2() {
    this.dataLoading2$.next(true)
    this.http.post(this.ENV.apiUrl + this.GET_DATA_ENDPOINT_2, {}).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resp: any) => {
        this.data2 = resp.data;
        this.selectedRows2 = []
        this.dataLoading2$.next(false)
      },
      error: () => {
        this.dataLoading2$.next(false)
      }
    })
  }

  startRebalancing() {
    this.rebalancingLoading$.next(true)
    this.setActiveTab("history", true)
    setTimeout(() => {
      this.refreshData2()
    }, 1000);
    this.http.post(this.ENV.apiUrl + 'calculateRebalancing', {}).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resp: any) => {
        this.rebalancingLoading$.next(false)
        if (this.formDialogVisible2 && this.formDialogData2?.historyId == resp.data.historyId) {
          this.formDialogData2 = resp.data
        }
      },
      error: () => {
        this.rebalancingLoading$.next(false)
      }
    })
  }


  calculatePredictions() {
    this.predictionsLoading$.next(true)
    this.http.post(this.ENV.apiUrl + 'calculatePredictions', {}).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resp: any) => {
        this.predictionsLoading$.next(false)
        this.refreshData()
      },
      error: () => {
        this.predictionsLoading$.next(false)
      }
    })
  }

  //? TABS
  setActiveTab(tab: string, fromUserAction: boolean = true) {
    if (tab != this.activeTab) {

      // Only navigate if change was triggered by user action
      if (fromUserAction) {
        this.router.navigate(['/admin', 'balancing', tab]);
      }
    }

    this.activeTab = tab;
  }


  //? POPUP
  openFormDialog2(record?: MAIN_I_2) {
    if (record) {
      this.dataLoading$.next(true)
      this.http.post(this.ENV.apiUrl + this.GET_RECORD_ENDPOINT_2, { ...record }).pipe(takeUntil(this.destroy$)).subscribe({
        next: (resp: any) => {
          this.selectedRows2 = []
          this.formDialogData2 = resp.data
          this.formDialogVisible2 = true;
          this.dataLoading2$.next(false)
        },
        error: () => {
          this.dataLoading2$.next(false)
        }
      })
    }
  }

  hideFormDialog2() {
    this.formDialogVisible2 = false;
    this.formDialogData2 = undefined;
  }

  openIndications() {
    if (!this.formDialogData2?.steps) return
    const steps = this.formDialogData2.steps
    const locations = steps.map(
      step => `${step.stationIdDetails.lat},${step.stationIdDetails.lng}`
    );

    if (locations.length < 2) {
      return
    }

    const origin = locations[0];
    const destination = locations[locations.length - 1];
    const waypoints = locations.slice(1, -1).join('|');

    const url = new URL('https://www.google.com/maps/dir/');
    url.searchParams.set('api', '1');
    url.searchParams.set('origin', origin);
    url.searchParams.set('destination', destination);
    if (waypoints) {
      url.searchParams.set('waypoints', waypoints);
    }

    window.open(url.toString(), '_blank');
  }


  //? ALTRO
  translateObjectValues<T>(obj: T, keyToTranslate: string): T {
    if (Array.isArray(obj)) {
      return obj.map(item => this.translateObjectValues(item, keyToTranslate)) as T;
    }

    if (obj && typeof obj === 'object') {
      const result = { ...obj };
      for (const [key, value] of Object.entries(result)) {
        if (key === keyToTranslate && typeof value === 'string') {
          (result as { [key: string]: any })[key] = this.transloco.translate(value);
        } else if (typeof value === 'object') {
          (result as { [key: string]: any })[key] = this.translateObjectValues(value, keyToTranslate);
        }
      }
      return result as T;
    }
    return obj;
  }

  getCellData(col: Column, data: any) {
    let pipe = col.pipe

    let value = getPathKey(data, col.nestedField ?? col.field, col.defaultValue ?? '<span class="null-chip">null</span>')

    if (pipe) {
      if (!Array.isArray(pipe)) {
        pipe = [pipe, {}]
      }
      let pipeconfig = pipe[1]
      pipeconfig = this.replaceObjectValues(pipeconfig, getPathKey(data, col.nestedField ?? col.field, col.defaultValue), data, value)
      switch (pipe[0]) {
        case "date":
          value = this.translocoDate.transform(value, pipeconfig, pipeconfig.locale);
          break

        case "currency":
          value = this.translocoCurrency.transform(value, pipeconfig.display, pipeconfig.numberFormatOptions, pipeconfig.currencyCode, pipeconfig.locale);
          break

        case "percent":
          value = this.translocoPercent.transform(value, pipeconfig, pipeconfig.locale);
          break

        case 'decimal':
          value = this.translocoDecimal.transform(value, pipeconfig, pipeconfig.locale);
          break

        case 'translate':
          value = this.transloco.translate(String(value), pipeconfig)
          break

        case 'duration':
          value = this.durationPipe.transform(value, pipeconfig);
          break

        case "custom":
          value = pipe[1](value)
          break

        default:
          return value;
      }
    }
    return value;
  }

  cleanDtoToSend(dto: any) {
    let res = structuredClone(dto)
    for (const key in res) {
      if (res[key] === undefined || res[key] === null || res[key] === "") {
        delete res[key]
      }
    }
    return res
  }

  parseWrapperInputs(col: Column, value: [any, any]) {
    let inputs = col.wrapperConfig;
    if (inputs) {
      inputs = structuredClone(inputs);
      const rowData = value[0];
      const currentColumnValue = value[1];
      const currentColumnValueOriginal = getPathKey(rowData, col.nestedField ?? col.field, col.defaultValue ?? '<span class="null-chip">null</span>');
      return this.replaceObjectValues(inputs, currentColumnValue, rowData, currentColumnValueOriginal)
    } else {
      return {};
    }
  }

  replaceObjectValues(obj: { [key: string]: any }, currentColumnValue: any, rowData: any, currentColumnValueOriginal: any) {
    if (typeof obj === "object" && obj !== null) {
      let objectCopy = structuredClone(obj);

      for (let key in objectCopy) {
        if (typeof objectCopy[key] === 'string') {
          // Replace {{columnField}} with the corresponding value from rowData
          objectCopy[key] = objectCopy[key].replace(/{{([\w.]+)}}/g, (match: any, path: any) => {
            if (path === 'value') {
              return currentColumnValue;
            }
            return getPathKey(rowData, path, match);
          });
        } else if (typeof objectCopy[key] === 'object') {
          // iterate thru key-val
          for (let subKey in objectCopy[key]) {
            if (typeof objectCopy[key][subKey] === 'string') {
              // Replace {{columnField}} with the corresponding value from rowData
              objectCopy[key][subKey] = objectCopy[key][subKey].replace(/{{([\w.]+)}}/g, (match: any, path: any) => {
                let res = currentColumnValue
                if (path !== 'value') {
                  res = getPathKey(rowData, path, match);
                }
                if (subKey == "--state-color" && res == "base") {
                  res = "var(--p-surface-100)"
                }
                if (subKey == "--state-color-text") {
                  res = res == "base" ? "var(--p-text-color)" : "var(--p-primary-color-text)"
                }
                return res
              });
            }
          }
        }

        const match = key.match(/^(.+)_if_(.+)_(.+)$/);
        if (match) {
          const baseKey = match[1];
          const compareType = match[2];
          const compareValue = match[3];

          let valueToCompare;
          if (compareType === 'val') {
            valueToCompare = currentColumnValueOriginal;
          } else {
            valueToCompare = getPathKey(rowData, compareType, undefined);
          }

          if (String(valueToCompare) === compareValue) {
            if (typeof objectCopy[key] == "string") {
              objectCopy[baseKey] = objectCopy[key].startsWith("%+t%") ?
                this.transloco.translate(objectCopy[key].replace("%+t%", "")) :
                objectCopy[key].replace("%+t%", "");
            } else {
              objectCopy[baseKey] = objectCopy[key]
            }

          }
          delete objectCopy[key];
        }
      }
      return objectCopy;
    }
    return obj
  }

  getStationAddressString(s?: Station, truncate?: number) {
    if (!s) return "--";
    let result = (s.address ? s.address + ", " : "") + (s.city ? s.city + (s.zip ? " (" + s.zip + ")" : "") : "");
    if (result.length == 0) {
      result = "--"
    } else if (truncate && result.length > truncate) {
      result = result.substring(0, truncate) + "...";
    }
    return result;
  }
}
